/*
  Basket drawer (docs/page-specs/cart.md, docs/cart.md). Opens on every add
  to basket and from the header basket icon; right-hand dialog, focus trapped
  (trapFocus from global.js), Escape and the overlay close it and focus goes
  back to whatever opened it. The markup is snippets/cart-drawer.liquid and
  every change re-renders it through the Section Rendering API: adds arrive
  with the cart-drawer section bundled in the /cart/add response
  (product-form.js calls renderContents), quantity changes and removals go
  through CartItems in cart.js. The "Add to your order" rail is fetched here
  from sections/cart-recommendations.liquid for the product most recently
  added and cached per product id so a quantity change does not refetch it.
*/
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.railCache = { productId: null, html: '' };
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]') || event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();

    // cart.js has already re-rendered the drawer for these; the closed drawer
    // catches up when it opens, so only an open one does the follow-up work.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-items' || !this.classList.contains('active')) return;
      this.afterRender();
      if (this.contains(document.activeElement)) return;
      trapFocus(this.dialog, this.querySelector('.cart-item__name') || this.dialog);
    });
  }

  // The dialog element; re-queried because renderContents replaces it.
  get dialog() {
    return this.querySelector('.drawer__inner');
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;

    // A frame between render and the class change so the slide transition runs.
    requestAnimationFrame(() => {
      this.classList.add('active');
      // Wait for the slide (or nothing under reduced motion) before moving focus.
      setTimeout(() => trapFocus(this.dialog, this.dialog), this.transitionDuration());
    });

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.loadRecommendations();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : null);
    document.body.classList.remove('overflow-hidden');
  }

  transitionDuration() {
    const value = getComputedStyle(this.dialog).transitionDuration || '0s';
    const seconds = parseFloat(value) || 0;
    return value.endsWith('ms') ? seconds : seconds * 1000;
  }

  // Called by product-form.js with the /cart/add response after an add.
  renderContents(parsedState) {
    const added = parsedState.items ? parsedState.items[0] : parsedState;
    if (added?.product_id) this.productId = String(added.product_id);

    const previousFill = this.querySelector('.free-delivery-bar__fill')?.style.width;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.classList.remove('is-empty');
    this.afterRender(previousFill);

    if (this.classList.contains('active')) {
      trapFocus(this.dialog, this.querySelector('.cart-item__name') || this.dialog);
    } else {
      this.open();
    }
  }

  // After any re-render: animate the free-delivery bar from its previous
  // width, refresh the view event payload, re-init accelerated checkout
  // buttons and refill the rail.
  afterRender(previousFill) {
    const payload = this.querySelector('[data-cart-view-payload]');
    const items = this.querySelector('cart-drawer-items');
    if (payload && items) items.setAttribute('view-event-payload', payload.dataset.cartViewPayload);

    const fill = this.querySelector('.free-delivery-bar__fill');
    if (fill && previousFill !== undefined) {
      const target = fill.style.width;
      fill.style.width = previousFill;
      // Force a layout so the width change below transitions.
      void fill.offsetWidth;
      fill.style.width = target;
    }
    if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
    this.loadRecommendations();
  }

  loadRecommendations() {
    const rail = this.querySelector('#CartDrawer-Rail');
    const url = this.dataset.recommendationsUrl;
    if (!rail || !url) return;

    // The placeholder carries the newest line's product id from Liquid; an
    // empty value means an empty basket. Right after an add the added product
    // wins, since an add can merge into an existing line further down.
    if (!rail.dataset.productId) {
      this.productId = null;
      rail.hidden = true;
      return;
    }
    // The added product only counts while its line is still in the basket.
    if (this.productId && !this.querySelector(`.cart-item[data-product-id="${this.productId}"]`)) {
      this.productId = null;
    }
    const productId = String(this.productId || rail.dataset.productId);
    if (this.railCache.productId === productId) {
      this.fillRail(rail, this.railCache.html);
      return;
    }
    if (this.railPending === productId) return;
    this.railPending = productId;

    fetch(`${url}?section_id=cart-recommendations&product_id=${productId}&limit=3&intent=complementary`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const html = text ? this.getSectionInnerHTML(text, '.cart-recommendations') : '';
        this.railCache = { productId, html };
        const currentRail = this.querySelector('#CartDrawer-Rail');
        if (currentRail) this.fillRail(currentRail, html);
      })
      .catch(() => {
        this.railCache = { productId, html: '' };
      })
      .finally(() => {
        if (this.railPending === productId) this.railPending = null;
      });
  }

  fillRail(rail, html) {
    if (rail.dataset.loadedFor === String(this.railCache.productId) && rail.innerHTML) return;
    rail.innerHTML = html;
    rail.dataset.loadedFor = String(this.railCache.productId);
    rail.hidden = !rail.querySelector('li');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  // The opener, restored on close. While the drawer is open a quick add from
  // the rail must not replace it with a button that is about to be re-rendered.
  setActiveElement(element) {
    if (this.classList.contains('active') && this.contains(element)) return;
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Adds arrive through CartDrawer.renderContents with the section bundled,
  // so an add must not also trigger the refetch in onCartUpdate.
  ignoresCartUpdate(event) {
    return event.source === 'cart-items' || event.source === 'product-form';
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: 'cart-drawer-items' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
